import { cn } from '@/lib/utils';

import BadgeSB from '@/components/atoms/badge/BadgeSB';
import Button from '@/components/atoms/button';
import Card from '@/components/atoms/card';
import { Divider } from '@/components/atoms/divider';
import { Dropdown, DropdownMenu } from '@/components/atoms/dropdown';
import ImageSB from '@/components/atoms/image/ImageSB';
import HeaderNavItemListSB from '@/components/atoms/layout/header/HeaderNavItemListSB';
import { HeaderProps } from '@/components/atoms/layout/header/HeaderSB';
import LinkButton from '@/components/atoms/linkButton';
import LinkButtonSB from '@/components/atoms/linkButton/LinkSB';
import { Text } from '@/components/atoms/text';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import './styles.css';

import IconChevronDown from '~/svg/IconChevronDown.svg';
import IconCross from '~/svg/IconCross.svg';

type HeaderItemSBProps = Required<HeaderProps['header']>['firstLevels'][number];

export type HeaderItemSBExternalProps = {
  isOpen: boolean;
  onClick: () => void;
};

export default function HeaderItemSB({
  block,
  ...rest
}: BlockComponentModel<HeaderItemSBProps> & HeaderItemSBExternalProps) {
  const { title, secondLevels, badge, extraInfo } = block;
  const { onClick, isOpen } = rest;

  function handleDropdownClick() {
    onClick?.();
  }

  return (
    <div>
      <Button
        classNames={{
          root: 'p-0 hover:text-neutral-N00 text-neutral-N40 h-auto font-sans group',
          inner: 'h-auto flex items-center gap-2',
        }}
        variant='text'
        onClick={handleDropdownClick}
      >
        <span className='text-body-l'>{title}</span>
        {badge && (
          <BadgeSB block={badge} inner={{ size: 's', variant: 'outlined' }} />
        )}
        <IconChevronDown
          className={cn(isOpen && 'rotate-180', 'transition-all')}
        />
      </Button>
      <Dropdown detached className={cn(isOpen ? 'visible' : 'collapse')}>
        <DropdownMenu
          classNames={{
            wrapper:
              'bg-neutral-N99/90 w-screen mt-4 px-10 py-12 backdrop-blur-[10px]',
          }}
        >
          <div className='mx-auto my-0 flex max-h-[650px] max-w-[1440px] justify-between'>
            <div className='flex w-full gap-9'>
              {secondLevels?.map((item, index) => (
                <div
                  key={index}
                  className='headerColumnScrollabilityHack flex gap-9 overflow-y-auto overflow-x-hidden'
                >
                  <HeaderNavItemListSB block={item} />
                  {(secondLevels.length - 1 !== index ||
                    (secondLevels.length - 1 === index && extraInfo)) && (
                    <Divider alignment='vertical' />
                  )}
                </div>
              ))}
              {/* TODO */}
              {extraInfo && (
                <div className='flex  flex-col flex-wrap gap-12'>
                  {extraInfo.map((item, index) => {
                    return (
                      <div
                        key={index}
                        className='flex max-w-[200px] flex-col gap-6'
                      >
                        {item.link ? (
                          <LinkButton
                            href={item.link.url}
                            variant='text'
                            color='tertiary'
                            showRightChevron
                            underline={false}
                            slim
                            classNames={{
                              root: 'text-label-text-l mb-3 text-neutral-N40',
                            }}
                          >
                            {item.title}
                          </LinkButton>
                        ) : (
                          <Text variant='label-text-l' color='neutral-N40'>
                            {item.title}
                          </Text>
                        )}
                        {item?.banner && (
                          <Card
                            classNames={{
                              root: 'flex flex-col p-6 gap-6 bg-transparent',
                            }}
                            color='gradient'
                          >
                            <div className='flex flex-col gap-3'>
                              <ImageSB
                                block={{ ...item.banner.icon }}
                                inner={{
                                  classNames: {
                                    image: 'w-6',
                                  },
                                }}
                              />
                              <Text variant='subtitle-m' color='neutral-N00'>
                                {item.banner.title}
                              </Text>
                              <Text variant='body-s' color='neutral-N40'>
                                {item.banner.description}
                              </Text>
                            </div>
                            <LinkButtonSB
                              block={item.banner.button}
                              inner={{
                                variant: 'outlined',
                                color: 'tertiary',
                              }}
                            />
                          </Card>
                        )}
                        {!!item?.list?.length && (
                          <div className='-ml-1 flex flex-wrap'>
                            {item.list.map((listItem, index) => (
                              <LinkButtonSB
                                block={listItem}
                                inner={{
                                  underline: false,
                                  variant: 'text',
                                  color: 'tertiary',
                                  classNames: {
                                    root: 'hover:bg-neutral-N94 px-1 h-auto text-body-s',
                                  },
                                }}
                                key={index}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <Button
              onClick={handleDropdownClick}
              classNames={{ root: 'text-neutral-N40 font-sans' }}
              variant='text'
              slim
            >
              <div className='h-6 w-6'>
                <IconCross />
              </div>
            </Button>
          </div>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}
